/**
 * WhatsApp buiten het 24-uursvenster (sessie 72, punt 10). Vrije tekst mag
 * dan niet; alleen een door Meta goedgekeurde template. De WhatsApp-stand
 * toont hier de keuze uit die templates, de invulvelden en hoe het bericht
 * eruit komt te zien. Versturen gaat over de bestaande weg
 * (`/api/admin/whatsapp-templates/verstuur`) met het gesprek van déze zaak,
 * zodat het bericht in de zaak staat. Geen nieuwe verzendweg.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { v6Api } from "../api";
import "./kanalen.css";

const GOEDGEKEURD = "goedgekeurd";

/** Hoogste `{{n}}` in de tekst: zoveel invulvelden. */
function aantalVariabelen(body: string): number {
  const nummers = [...body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  return nummers.length ? Math.max(...nummers) : 0;
}

/** Zoals de server hem archiveert (`render_body`); leeg veld blijft `{{n}}`. */
function vulIn(body: string, variabelen: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (heel, n: string) => {
    const waarde = variabelen[Number(n) - 1]?.trim();
    return waarde ? waarde : heel;
  });
}

export interface TemplateKeuzeStand {
  laadt: boolean;
  fout: string | null;
  templates: { id: string; naam: string; body_tekst: string; variabele_labels: string[] }[];
  gekozen: string | null;
  kies: (id: string) => void;
  variabelen: string[];
  zetVariabele: (i: number, waarde: string) => void;
  voorbeeld: string | null;
  /** Waarom er nog niets weg kan, of null. */
  melding: string | null;
  kan: boolean;
  verstuur: () => Promise<unknown>;
  leeg: () => void;
}

export function useTemplateKeuze(sessieId: string | null, voornaam: string | null): TemplateKeuzeStand {
  const lijst = useQuery({
    queryKey: ["v6", "whatsapp-templates"],
    queryFn: () => v6Api.whatsappTemplates(),
    enabled: Boolean(sessieId),
    staleTime: 5 * 60_000,
  });
  const templates = (lijst.data ?? []).filter((t) => t.status === GOEDGEKEURD);
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [variabelen, setVariabelen] = useState<string[]>([]);
  const template = templates.find((t) => t.id === gekozen) ?? null;
  const aantal = template ? aantalVariabelen(template.body_tekst) : 0;
  const open = variabelen.slice(0, aantal).filter((v) => v?.trim()).length < aantal;

  const kies = (id: string) => {
    setGekozen(id);
    const t = templates.find((x) => x.id === id);
    const n = t ? aantalVariabelen(t.body_tekst) : 0;
    // Het eerste veld is bijna altijd de voornaam: alvast ingevuld.
    setVariabelen(Array.from({ length: n }, (_, i) => (i === 0 && voornaam ? voornaam : "")));
  };

  const status = (lijst.error as { response?: { status?: number } } | null)?.response?.status;
  const fout = lijst.isError
    ? status === 403
      ? "Alleen een beheerder kan een WhatsApp-template versturen."
      : "De templates konden niet geladen worden."
    : null;

  let melding: string | null = null;
  if (!sessieId) melding = "Er is geen WhatsApp-gesprek met deze klant.";
  else if (fout) melding = null;
  else if (!lijst.isPending && templates.length === 0)
    melding = "Er is geen goedgekeurde template; vraag er een aan onder Beheer, WhatsApp-sjablonen.";
  else if (template && open) melding = "Vul alle velden van de template in.";

  return {
    laadt: lijst.isPending && Boolean(sessieId),
    fout,
    templates,
    gekozen,
    kies,
    variabelen,
    zetVariabele: (i, waarde) =>
      setVariabelen((oud) => {
        const kopie = [...oud];
        kopie[i] = waarde;
        return kopie;
      }),
    voorbeeld: template ? vulIn(template.body_tekst, variabelen) : null,
    melding,
    kan: Boolean(sessieId && template && !open),
    verstuur: () =>
      v6Api.verstuurTemplate({
        template_id: template?.id ?? "",
        variabelen: variabelen.slice(0, aantal).map((v) => v.trim()),
        sessie_id: sessieId ?? "",
      }),
    leeg: () => {
      setGekozen(null);
      setVariabelen([]);
    },
  };
}

/** De keuze zelf: rondjes per template, invulvelden, en het bericht. */
export function TemplateKeuze({ keuze }: { keuze: TemplateKeuzeStand }) {
  const template = keuze.templates.find((t) => t.id === keuze.gekozen) ?? null;
  return (
    <div className="templatekeuze" data-testid="v6-templatekeuze">
      <p className="templateuitleg">
        Het venster van 24 uur is dicht. Kies een goedgekeurde template; de klant kan daarna
        gewoon antwoorden.
      </p>
      {keuze.laadt && <p className="v6-leeg">Templates laden</p>}
      {keuze.fout && (
        <p className="v6-fout" role="alert">
          {keuze.fout}
        </p>
      )}
      {keuze.templates.length > 0 && (
        <div role="radiogroup" aria-label="Template" className="keuzes">
          {keuze.templates.map((t) => (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={t.id === keuze.gekozen}
              className={`keuze${t.id === keuze.gekozen ? " on" : ""}`}
              onClick={() => keuze.kies(t.id)}
            >
              <span className="rond" aria-hidden="true" />
              <span className="keuzetekst">{t.naam.replace(/_/g, " ")}</span>
              <small>{t.body_tekst}</small>
            </button>
          ))}
        </div>
      )}
      {template &&
        keuze.variabelen.map((waarde, i) => {
          const label = template.variabele_labels[i] || `Veld ${i + 1}`;
          return (
            <div className="rij" key={`${template.id}-${i}`}>
              <span className="l">{label}</span>
              <input aria-label={label} value={waarde} onChange={(e) => keuze.zetVariabele(i, e.target.value)} />
              <span />
            </div>
          );
        })}
      {keuze.voorbeeld && (
        <div className="templatevoorbeeld" aria-label="Zo komt het bericht aan">
          {keuze.voorbeeld}
        </div>
      )}
    </div>
  );
}
